import { useState, type Dispatch, type ReactNode, type SetStateAction } from 'react';
import { Button } from '@clay-run/glaze/button';
import { CheckboxField } from '@clay-run/glaze/checkbox-field';
import { ComboboxMultipleOption } from '@clay-run/glaze/combobox-multiple';
import { ComboboxMultipleField } from '@clay-run/glaze/combobox-multiple-field';
import { InputField } from '@clay-run/glaze/input-field';
import { SegmentedControl, SegmentedControlOption } from '@clay-run/glaze/segmented-control';
import { SelectField } from '@clay-run/glaze/select-field';
import { Tab, TabList, Tabs } from '@clay-run/glaze/tabs';
import { Text } from '@clay-run/glaze/text';
import { TextareaField } from '@clay-run/glaze/textarea-field';
import { Lock } from '@phosphor-icons/react/dist/icons/Lock';
import { Plus } from '@phosphor-icons/react/dist/icons/Plus';
import { X } from '@phosphor-icons/react/dist/icons/X';
import { AUTOFILL_IGNORE_PROPS } from '../../_shared/autofill';
import {
  useCreativeCheck,
  useLinkedInCampaignGroups,
  useLinkedInCampaigns,
  useLinkedInPost,
  useMetaAudiences,
  useMetaStatus,
  useMetaVideo,
} from '../api-client';
import { DEFAULT_EXCLUSIONS, DEPRECATED_AUDIENCES, personaOf, resolveByName } from '../audiences';
import {
  creativeCopy,
  hasOverrides,
  newCreative,
  resolvedExclusions,
  withNaming,
  withPersonas,
  type DraftCreative,
  type MetaImage,
} from '../draft';
import { adName } from '../naming';
import {
  LINKEDIN_GROWTH_DEFAULTS,
  countryName,
  metaGrowthChips,
  META_HEADLINE_LIMIT,
  META_PRIMARY_TEXT_VISIBLE_LIMIT,
  type LinkedInDraft,
  type MetaDraft,
} from '../draft-fixtures';
import {
  AD_TEAMS,
  BOOSTABLE_CAMPAIGN_TYPE,
  MAX_COUNTRIES,
  MAX_CREATIVES,
  MAX_VIDEO_BYTES,
  META_BID_STRATEGIES,
  META_BID_STRATEGY_LABELS,
  META_CTA_LABELS,
  META_CTA_TYPES,
  MetaBidStrategySchema,
  MetaCtaTypeSchema,
  NO_OVERRIDES,
  PERSONAS,
  type MetaAudience,
  type MetaAudienceRef,
  type MetaGrowthSettings,
  type Persona,
} from '../types';
import { META_VERTICAL_RATIO, describeAspectRatio, matchesRatio } from '../platforms';
import { dataUrlToFile, imageDimensions, imageForReview, videoFrame } from '../image-tools';
import { LinkedInPostPreview, MetaFeedPreview, MetaStoryPreview } from './AdPreviews';
import { CreativeCheckCard } from './CreativeCheckCard';
import { LeadFormCard } from './LeadFormCard';

const META_CTA_ITEMS = META_CTA_TYPES.map(value => ({ value, label: META_CTA_LABELS[value] }));

export type PlatformTab = 'meta' | 'linkedin';

function GrowthDefaults({ items, action }: { items: string[]; action?: ReactNode }) {
  return (
    <div className="flex flex-col gap-2.5 rounded-xl bg-bg-secondary p-3.5">
      <div className="flex items-center gap-2 text-sm font-medium">
        <Lock size={16} />
        Growth defaults
        <span className="font-normal text-content-tertiary">· set by reviewers</span>
        {action && <span className="ml-auto">{action}</span>}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {items.map(item => (
          <span key={item} className="rounded-md bg-bg-primary px-2 py-1 text-xs">
            {item}
          </span>
        ))}
      </div>
    </div>
  );
}

// Countries Growth runs in most; any other one can be added by its two-letter code.
const COUNTRY_CODES = ['US', 'CA', 'GB', 'IE', 'AU', 'NZ', 'DE', 'FR', 'NL', 'BE', 'ES', 'IT', 'PT', 'CH', 'AT', 'SE', 'DK', 'NO', 'FI', 'PL', 'IL', 'AE', 'IN', 'SG', 'JP', 'BR', 'MX', 'ZA'];
// Meta allows "Maximize number of leads" only for ads that open an Instant Form, so the objective
// and the destination are one choice.
const OBJECTIVE_ITEMS: { value: MetaDraft['destination']; label: string }[] = [
  { value: 'lead_form', label: 'Leads · Maximize number of leads (Instant Form)' },
  { value: 'website', label: 'Traffic · Landing page views (website)' },
];
const BID_ITEMS = META_BID_STRATEGIES.map(value => ({ value, label: META_BID_STRATEGY_LABELS[value] }));

function PersonaSelect({ value, onChange }: { value: Persona[]; onChange: (personas: Persona[]) => void }) {
  const [query, setQuery] = useState('');
  const items = PERSONAS.filter(p => p.toLowerCase().includes(query.trim().toLowerCase()));
  return (
    <ComboboxMultipleField<Persona>
      label="Personas"
      placeholder={value.length === 0 ? 'Pick personas' : undefined}
      items={items}
      // Kept in Growth's persona order, so names read the same whichever was picked first.
      value={value}
      onValueChange={next => onChange(PERSONAS.filter(p => next.includes(p)))}
      itemToStringLabel={p => p}
      inputValue={query}
      onInputValueChange={setQuery}
    >
      {items.map(p => (
        <ComboboxMultipleOption key={p} value={p}>
          {p}
        </ComboboxMultipleOption>
      ))}
    </ComboboxMultipleField>
  );
}

function TeamSelect({ value, onChange }: { value: string; onChange: (team: string) => void }) {
  // A team saved before the list existed stays selectable.
  const teams: string[] = value && !AD_TEAMS.some(t => t === value) ? [...AD_TEAMS, value] : [...AD_TEAMS];
  return (
    <SelectField
      label="Team"
      placeholder="Pick a team"
      items={teams.map(t => ({ value: t, label: t }))}
      value={value || undefined}
      onValueChange={v => onChange(String(v))}
    />
  );
}

/** Growth's targeting and bidding: read-only for requesters, editable for reviewers. */
function MetaGrowthSettingsCard({
  destination,
  onDestinationChange,
  settings,
  onChange,
  editable,
}: {
  destination: MetaDraft['destination'];
  onDestinationChange: (d: MetaDraft['destination']) => void;
  settings: MetaGrowthSettings;
  onChange: (s: MetaGrowthSettings) => void;
  editable: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [countryQuery, setCountryQuery] = useState('');
  const query = countryQuery.trim().toUpperCase();
  const codes = [...new Set([...settings.countries, ...COUNTRY_CODES, ...(/^[A-Z]{2}$/.test(query) ? [query] : [])])];
  const countries = query ? codes.filter(c => c.includes(query) || countryName(c).toUpperCase().includes(query)) : codes;
  const set = <K extends keyof MetaGrowthSettings>(key: K, value: MetaGrowthSettings[K]) => onChange({ ...settings, [key]: value });
  const age = (value: string) => Math.min(65, Math.max(18, Math.round(Number(value) || 18)));

  return (
    <div className="flex flex-col gap-3">
      <GrowthDefaults
        items={metaGrowthChips(destination, settings)}
        action={
          editable && (
            <Button size="sm" variant="ghost" onClick={() => setEditing(e => !e)}>
              {editing ? 'Done' : 'Edit'}
            </Button>
          )
        }
      />
      {editable && editing && (
        <div className="flex flex-col gap-3 rounded-xl border border-border-primary p-3.5">
          <SelectField
            label="Objective and performance goal"
            description={destination === 'lead_form' ? 'The ad opens an Instant Form; pick it under "Send people to".' : undefined}
            items={OBJECTIVE_ITEMS}
            value={destination}
            onValueChange={v => onDestinationChange(v === 'website' ? 'website' : 'lead_form')}
          />
          <ComboboxMultipleField<string>
            label="Countries"
            description={`Up to ${MAX_COUNTRIES}.`}
            items={countries}
            value={settings.countries}
            onValueChange={v => v.length > 0 && v.length <= MAX_COUNTRIES && set('countries', v)}
            itemToStringLabel={countryName}
            inputValue={countryQuery}
            onInputValueChange={setCountryQuery}
          >
            {countries.map(c => (
              <ComboboxMultipleOption key={c} value={c}>
                {countryName(c)} ({c})
              </ComboboxMultipleOption>
            ))}
          </ComboboxMultipleField>
          <div className="grid grid-cols-2 gap-3">
            <InputField
              {...AUTOFILL_IGNORE_PROPS}
              label="Minimum age"
              type="number"
              value={String(settings.ageMin)}
              onValueChange={v => onChange({ ...settings, ageMin: age(v), ageMax: Math.max(age(v), settings.ageMax) })}
            />
            <InputField
              {...AUTOFILL_IGNORE_PROPS}
              label="Maximum age"
              type="number"
              value={String(settings.ageMax)}
              onValueChange={v => onChange({ ...settings, ageMax: age(v), ageMin: Math.min(age(v), settings.ageMin) })}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <SelectField
              label="Bid strategy"
              items={BID_ITEMS}
              value={settings.bidStrategy}
              onValueChange={v => {
                const bidStrategy = MetaBidStrategySchema.parse(v);
                onChange({ ...settings, bidStrategy, bidAmountUsd: bidStrategy === 'COST_CAP' ? (settings.bidAmountUsd ?? 50) : null });
              }}
            />
            {settings.bidStrategy === 'COST_CAP' && (
              <InputField
                {...AUTOFILL_IGNORE_PROPS}
                label="Cost per result goal (USD)"
                type="number"
                value={String(settings.bidAmountUsd ?? '')}
                onValueChange={v => set('bidAmountUsd', Math.max(0.01, Number(v) || 0.01))}
              />
            )}
          </div>
          <CheckboxField
            label="Advantage+ audience"
            description="Lets Meta reach beyond the audience and targeting when it expects better results."
            checked={settings.advantageAudience}
            onCheckedChange={checked => set('advantageAudience', checked === true)}
          />
        </div>
      )}
    </div>
  );
}

const audienceKind = (a: MetaAudience | undefined) => [a && personaOf(a.name), a?.subtype === 'LOOKALIKE' ? 'Lookalike' : null].filter(Boolean).join(' · ');

function AudienceMultiSelect({
  label,
  description,
  value,
  onChange,
  audiences,
  disabled,
  loading,
}: {
  label: string;
  description: string;
  value: MetaAudienceRef[];
  onChange: (audiences: MetaAudienceRef[]) => void;
  audiences: MetaAudience[];
  disabled: boolean;
  loading: boolean;
}) {
  const [query, setQuery] = useState('');
  const byId = new Map(audiences.map(a => [a.id, a]));
  const q = query.trim().toLowerCase();
  // Saved picks stay listed even when the account no longer offers them (e.g. deprecated ones).
  const refs = [...value, ...audiences.filter(a => !value.some(v => v.id === a.id)).map(a => ({ id: a.id, name: a.name }))];
  const items = q ? refs.filter(r => r.name.toLowerCase().includes(q)) : refs;
  return (
    <ComboboxMultipleField<MetaAudienceRef>
      label={label}
      description={description}
      items={items}
      value={value}
      onValueChange={onChange}
      itemToStringLabel={r => r.name}
      inputValue={query}
      onInputValueChange={setQuery}
      by="id"
      disabled={disabled}
      loading={loading}
    >
      {items.map(r => {
        const audience = byId.get(r.id);
        const kind = audience ? audienceKind(audience) : DEPRECATED_AUDIENCES.has(r.name) ? 'Deprecated' : 'Not in this account';
        const size = audience?.approximateCount != null ? `~${audience.approximateCount.toLocaleString()}` : null;
        return (
          <ComboboxMultipleOption key={r.id} value={r}>
            <span className="flex min-w-0 flex-col">
              <span className="truncate">{r.name}</span>
              {(kind || size) && (
                <span className="truncate text-xs text-content-tertiary">{[kind, size].filter(Boolean).join(' · ')}</span>
              )}
            </span>
          </ComboboxMultipleOption>
        );
      })}
    </ComboboxMultipleField>
  );
}

/**
 * Who the ad set targets and leaves out. Growth's default exclusions start selected; a requester who
 * removes one says why, a reviewer doesn't have to.
 */
function AudienceTargeting({ draft, onChange, enabled, reviewer }: { draft: MetaDraft; onChange: (d: MetaDraft) => void; enabled: boolean; reviewer: boolean }) {
  const audiences = useMetaAudiences(enabled);
  const all = audiences.data ?? [];
  const exclusions = resolvedExclusions(draft, all);
  const defaults = resolveByName(all, DEFAULT_EXCLUSIONS);
  const disabled = !enabled || audiences.isLoading;

  const setExclusions = (next: MetaAudienceRef[]) => {
    const names = new Set(next.map(a => a.name));
    const reasonFor = (name: string) => draft.droppedExclusions.find(d => d.name === name) ?? { name, reason: '' };
    const droppedExclusions = reviewer
      ? draft.droppedExclusions.filter(d => !names.has(d.name))
      : defaults.found.filter(d => !names.has(d.name)).map(d => reasonFor(d.name));
    onChange({ ...draft, exclusions: next, droppedExclusions });
  };
  const setReason = (name: string, reason: string) =>
    onChange({ ...draft, droppedExclusions: draft.droppedExclusions.map(d => (d.name === name ? { ...d, reason } : d)) });

  return (
    <div className="flex flex-col gap-3">
      <AudienceMultiSelect
        label="Target audiences"
        description={
          !enabled
            ? 'Connect Meta to pick audiences.'
            : 'Clay Audiences and lookalikes synced to Meta. Picking personas above fills these in.'
        }
        value={draft.targets}
        onChange={targets => onChange({ ...draft, targets, targetsEdited: true })}
        audiences={all}
        disabled={disabled}
        loading={audiences.isLoading}
      />
      <AudienceMultiSelect
        label="Excluded audiences"
        description={
          reviewer
            ? "Growth's default exclusions start selected."
            : "Growth's default exclusions start selected. Removing one needs a reason for the reviewer."
        }
        value={exclusions}
        onChange={setExclusions}
        audiences={all}
        disabled={disabled}
        loading={audiences.isLoading}
      />
      {draft.droppedExclusions.map(d => (
        <InputField
          {...AUTOFILL_IGNORE_PROPS}
          key={d.name}
          label={`Why drop ${d.name}?`}
          description="The reviewer sees this before publishing."
          value={d.reason}
          onValueChange={reason => setReason(d.name, reason)}
        />
      ))}
      {enabled && audiences.data && defaults.missing.length > 0 && (
        <Text size="xs" variant="tertiary">
          Not in this ad account, so not excluded: {defaults.missing.join(', ')}.
        </Text>
      )}
    </div>
  );
}

export const IMAGE_INPUT_ID = 'adcc-meta-image';
const VERTICAL_INPUT_ID = 'adcc-meta-image-vertical';
const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png'];
const ACCEPTED_VIDEO_TYPES = ['video/mp4', 'video/quicktime'];
const MAX_IMAGE_BYTES = 8 * 1024 * 1024;

async function readVideo(file: File): Promise<MetaImage> {
  if (file.size > MAX_VIDEO_BYTES) {
    throw new Error(`${file.name} is ${(file.size / 1_000_000_000).toFixed(1)} GB; the limit is 1 GB.`);
  }
  const url = URL.createObjectURL(file);
  try {
    const frame = await videoFrame(url);
    return {
      file: dataUrlToFile(frame.dataUrl, 'thumbnail.jpg'),
      previewUrl: frame.dataUrl,
      width: frame.width,
      height: frame.height,
      video: { file, url, id: null },
    };
  } catch (err) {
    URL.revokeObjectURL(url);
    throw err;
  }
}

function readImage(file: File): Promise<MetaImage> {
  if (ACCEPTED_VIDEO_TYPES.includes(file.type)) return readVideo(file);
  if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
    return Promise.reject(new Error(`${file.name} is ${file.type || 'an unknown type'}. Meta ads need a JPEG, PNG, MP4 or MOV.`));
  }
  if (file.size > MAX_IMAGE_BYTES) {
    return Promise.reject(new Error(`${file.name} is ${(file.size / 1_000_000).toFixed(1)} MB; the limit is 8 MB.`));
  }
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () =>
      typeof reader.result === 'string' ? resolve(reader.result) : reject(new Error(`${file.name} couldn't be read.`));
    reader.onerror = () => reject(new Error(`${file.name} couldn't be read.`));
    reader.readAsDataURL(file);
  }).then(async previewUrl => ({ file, previewUrl, ...(await imageDimensions(previewUrl)) }));
}

export type MediaSlot = 'feed' | 'vertical';

/** `take` reads a file into a creative's slot; the creative with `key` stays the target if the selection moves. */
function useImageIntake(onPicked: (image: MetaImage, key: string, slot: MediaSlot) => void) {
  const [error, setError] = useState<{ slot: MediaSlot; message: string } | null>(null);
  const take = (file: File | undefined, key: string, slot: MediaSlot, kind: 'image' | 'video' | null = null) => {
    if (!file) return;
    if (kind && ACCEPTED_VIDEO_TYPES.includes(file.type) !== (kind === 'video')) {
      setError({ slot, message: `The 9:16 version has to be ${kind === 'video' ? 'a video' : 'an image'}, like the feed version.` });
      return;
    }
    readImage(file).then(
      image => {
        setError(null);
        onPicked(image, key, slot);
      },
      (err: unknown) => setError({ slot, message: err instanceof Error ? err.message : String(err) }),
    );
  };
  return { error, take, fail: setError };
}

function ImagePicker({
  inputId = IMAGE_INPUT_ID,
  label = 'Image or video',
  accept = [...ACCEPTED_IMAGE_TYPES, ...ACCEPTED_VIDEO_TYPES],
  emptyHint,
  image,
  error,
  onFile,
  onRemove,
}: {
  inputId?: string;
  label?: string;
  accept?: string[];
  emptyHint?: string;
  image: MetaImage | null;
  error: string | null;
  onFile: (file: File | undefined) => void;
  onRemove?: () => void;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2">
        <label htmlFor={inputId} className="text-sm font-medium">
          {label}
        </label>
        {image && onRemove && (
          <Button variant="inline-text" size="xs" onClick={onRemove}>
            Remove
          </Button>
        )}
      </div>
      <input
        id={inputId}
        type="file"
        accept={accept.join(',')}
        className="text-sm file:mr-3 file:rounded-md file:border-0 file:bg-bg-secondary file:px-3 file:py-1.5 file:text-sm file:font-medium"
        onChange={e => {
          onFile(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
      {error ? (
        <Text size="xs" className="text-text-red">
          {error}
        </Text>
      ) : (
        <Text size="xs" variant="tertiary">
          {image?.video?.file
            ? `${image.video.file.name} · ${(image.video.file.size / 1_000_000).toFixed(1)} MB video`
            : image?.file
              ? `${image.file.name} · ${(image.file.size / 1_000_000).toFixed(1)} MB`
              : image
                ? `Current ${image.video ? 'video' : 'image'}. Pick a file or drop one on the preview to replace it.`
                : (emptyHint ??
                  'Image: JPEG or PNG, 1:1 or 4:5, up to 8 MB. Video: MP4 or MOV, 4:5, up to 1 GB. You can also drop it on the preview.')}
        </Text>
      )}
    </div>
  );
}

function CreativesStrip({
  creatives,
  selectedKey,
  onSelect,
  onAdd,
  onRemove,
}: {
  creatives: DraftCreative[];
  selectedKey: string;
  onSelect: (key: string) => void;
  onAdd: () => void;
  onRemove: (key: string) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {creatives.map(c => (
        <div
          key={c.key}
          className={`flex items-center gap-1 rounded-lg border py-1 pl-1 pr-1.5 text-sm ${
            c.key === selectedKey ? 'border-border-action bg-bg-secondary' : 'border-border-primary'
          }`}
        >
          <button type="button" className="flex items-center gap-2" onClick={() => onSelect(c.key)}>
            {c.media ? (
              <img src={c.media.previewUrl} alt="" className="size-7 rounded object-cover" />
            ) : (
              <span className="size-7 rounded bg-bg-secondary" />
            )}
            <span className="max-w-32 truncate">{c.name || 'Unnamed'}</span>
          </button>
          {creatives.length > 1 && (
            <button
              type="button"
              aria-label={`Remove ${c.name || 'creative'}`}
              className="rounded p-0.5 text-content-tertiary hover:bg-bg-secondary"
              onClick={() => onRemove(c.key)}
            >
              <X size={12} />
            </button>
          )}
        </div>
      ))}
      <Button variant="ghost" size="sm" onClick={onAdd} disabled={creatives.length >= MAX_CREATIVES}>
        <Plus size={14} />
        Add creative
      </Button>
    </div>
  );
}

function SelectedCreativeFields({
  draft,
  creative,
  multiple,
  onChange,
}: {
  draft: MetaDraft;
  creative: DraftCreative;
  multiple: boolean;
  onChange: (c: DraftCreative) => void;
}) {
  const setOverride = (key: keyof DraftCreative['overrides']) => (value: string) =>
    onChange({ ...creative, overrides: { ...creative.overrides, [key]: value } });
  const own = hasOverrides(creative);
  return (
    <>
      <InputField
        {...AUTOFILL_IGNORE_PROPS}
        label="Creative"
        description={`e.g. video3 or HingeMatchRate_Static. The ad is named ${adName(draft.program, draft.personas, creative.name || 'creative')}.`}
        value={creative.name}
        onValueChange={name => onChange({ ...creative, name })}
      />
      {(multiple || own) && (
        <CheckboxField
          label="Different copy for this ad"
          description="Otherwise it runs with the shared copy above."
          checked={own}
          onCheckedChange={checked =>
            onChange({
              ...creative,
              overrides: checked ? { primaryText: draft.primaryText, headline: draft.headline, description: null } : NO_OVERRIDES,
            })
          }
        />
      )}
      {own && (
        <div className="flex flex-col gap-3 border-l-2 border-border-primary pl-3">
          <TextareaField
            label="Primary text for this ad"
            rows={3}
            value={creative.overrides.primaryText ?? ''}
            onValueChange={setOverride('primaryText')}
          />
          <InputField
            {...AUTOFILL_IGNORE_PROPS}
            label="Headline for this ad"
            value={creative.overrides.headline ?? ''}
            onValueChange={setOverride('headline')}
          />
          <InputField
            {...AUTOFILL_IGNORE_PROPS}
            label="Description for this ad"
            description="Leave empty to use the shared description."
            value={creative.overrides.description ?? ''}
            onValueChange={setOverride('description')}
          />
        </div>
      )}
    </>
  );
}

function verticalRatioProblem(image: MetaImage | null): string | null {
  if (!image || image.width === undefined || image.height === undefined) return null;
  if (matchesRatio(image.width, image.height, META_VERTICAL_RATIO)) return null;
  return `This is ${describeAspectRatio(image.width, image.height)}; Stories and Reels need ${META_VERTICAL_RATIO.label}, e.g. 1080×1920.`;
}

function VerticalSlot({
  creative,
  error,
  onFile,
  onRemove,
}: {
  creative: DraftCreative;
  error: string | null;
  onFile: (file: File | undefined) => void;
  onRemove: () => void;
}) {
  if (!creative.media) {
    return (
      <Text size="xs" variant="tertiary">
        Add the feed version first, then its 9:16 version for Stories and Reels.
      </Text>
    );
  }
  const video = creative.media.video !== undefined;
  const ratioProblem = verticalRatioProblem(creative.vertical);
  return (
    <div className="flex flex-col gap-1">
      <ImagePicker
        inputId={VERTICAL_INPUT_ID}
        label={`Stories & Reels (${META_VERTICAL_RATIO.label})`}
        accept={video ? ACCEPTED_VIDEO_TYPES : ACCEPTED_IMAGE_TYPES}
        emptyHint={
          video
            ? 'Optional, but Meta recommends it: the same video cut to 9:16 (MP4 or MOV), so Stories and Reels don\'t crop the feed one.'
            : 'Optional, but Meta recommends it: the same image at 9:16 (JPEG or PNG), so Stories and Reels don\'t crop the feed one.'
        }
        image={creative.vertical}
        error={error}
        onFile={onFile}
        onRemove={onRemove}
      />
      {ratioProblem && (
        <Text size="xs" variant="warning">
          {ratioProblem}
        </Text>
      )}
    </div>
  );
}

function MetaForm({
  draft,
  onChange,
  creatives,
  selected,
  onSelect,
  onCreativesChange,
  imageError,
  onImageFile,
  creativeCheck,
  showCreativeCheck,
  metaConnected,
}: {
  draft: MetaDraft;
  onChange: (d: MetaDraft) => void;
  creatives: DraftCreative[];
  selected: DraftCreative;
  onSelect: (key: string) => void;
  onCreativesChange: Dispatch<SetStateAction<DraftCreative[]>>;
  imageError: { slot: MediaSlot; message: string } | null;
  onImageFile: (file: File | undefined, slot: MediaSlot) => void;
  creativeCheck: ReturnType<typeof useCreativeCheck>;
  showCreativeCheck: boolean;
  metaConnected: boolean;
}) {
  const set = <K extends keyof MetaDraft>(key: K) => (value: MetaDraft[K]) => onChange({ ...draft, [key]: value });
  const multiple = creatives.length > 1;
  const canEditSettings = useMetaStatus().data?.canPublish ?? false;
  const audiences = useMetaAudiences(metaConnected);
  const updateSelected = (c: DraftCreative) => onCreativesChange(cs => cs.map(x => (x.key === c.key ? c : x)));

  return (
    <div className="flex min-w-0 flex-1 flex-col gap-4">
      <div className="grid grid-cols-[minmax(0,1fr)_160px_160px] gap-3">
        <InputField
          {...AUTOFILL_IGNORE_PROPS}
          label="Program"
          description="What's promoted, as Growth names it, e.g. HCUC Ep15 Territory Planning."
          value={draft.program}
          onValueChange={program => onChange(withNaming({ ...draft, program }))}
        />
        <PersonaSelect value={draft.personas} onChange={personas => onChange(withPersonas(draft, personas, audiences.data ?? []))} />
        <TeamSelect value={draft.team} onChange={set('team')} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <InputField
          {...AUTOFILL_IGNORE_PROPS}
          label="Campaign name"
          description={draft.namesEdited ? undefined : 'FB Ads - [program] - OBJ:Leads'}
          value={draft.name}
          onValueChange={name => onChange({ ...draft, name, namesEdited: true })}
        />
        <InputField
          {...AUTOFILL_IGNORE_PROPS}
          label="Ad set name"
          description={draft.namesEdited ? undefined : '[program] - Lead Gen - [persona]'}
          value={draft.adSetName}
          onValueChange={adSetName => onChange({ ...draft, adSetName, namesEdited: true })}
        />
      </div>
      <TextareaField
        label="Primary text"
        description={`${draft.primaryText.length} / ${META_PRIMARY_TEXT_VISIBLE_LIMIT} visible${multiple ? ' · shared by every ad' : ''}`}
        rows={3}
        value={draft.primaryText}
        onValueChange={set('primaryText')}
      />
      <InputField
        {...AUTOFILL_IGNORE_PROPS}
        label="Headline"
        description={`${draft.headline.length} / ${META_HEADLINE_LIMIT}`}
        value={draft.headline}
        onValueChange={set('headline')}
      />
      <InputField {...AUTOFILL_IGNORE_PROPS} label="Description" value={draft.description} onValueChange={set('description')} />
      <div className="flex flex-col gap-3 rounded-xl border border-border-primary p-3.5">
        <div className="flex flex-col gap-0.5">
          <Text size="sm" className="font-medium">
            Creatives
          </Text>
          <Text size="xs" variant="tertiary">
            Each one becomes its own ad in the same ad set, and Meta rotates between them. Ads are created switched off
            (paused): nothing runs or spends until Growth turns them on in Ads Manager. Give each a 4:5 version for feeds
            and a 9:16 one for Stories and Reels.
          </Text>
        </div>
        <CreativesStrip
          creatives={creatives}
          selectedKey={selected.key}
          onSelect={onSelect}
          onAdd={() => {
            const added = newCreative(creatives);
            onCreativesChange(cs => [...cs, added]);
            onSelect(added.key);
          }}
          onRemove={key => {
            const rest = creatives.filter(c => c.key !== key);
            onCreativesChange(rest);
            if (key === selected.key && rest[0]) onSelect(rest[0].key);
          }}
        />
        <SelectedCreativeFields draft={draft} creative={selected} multiple={multiple} onChange={updateSelected} />
        <ImagePicker
          label="Feed (4:5 or 1:1)"
          image={selected.media}
          error={imageError?.slot === 'feed' ? imageError.message : null}
          onFile={file => onImageFile(file, 'feed')}
        />
        <VerticalSlot
          creative={selected}
          error={imageError?.slot === 'vertical' ? imageError.message : null}
          onFile={file => onImageFile(file, 'vertical')}
          onRemove={() => updateSelected({ ...selected, vertical: null })}
        />
        {showCreativeCheck && (
          <CreativeCheckCard
            check={creativeCheck}
            onApply={suggestion => {
              if (hasOverrides(selected)) {
                updateSelected({
                  ...selected,
                  overrides: {
                    primaryText: suggestion.primaryText ?? selected.overrides.primaryText,
                    headline: suggestion.headline ?? selected.overrides.headline,
                    description: suggestion.description ?? selected.overrides.description,
                  },
                });
                return;
              }
              onChange({
                ...draft,
                ...(suggestion.primaryText && { primaryText: suggestion.primaryText }),
                ...(suggestion.headline && { headline: suggestion.headline }),
                ...(suggestion.description && { description: suggestion.description }),
              });
            }}
          />
        )}
      </div>
      <div className="flex flex-col gap-1.5">
        <Text size="sm" weight="medium">
          Send people to
        </Text>
        <SegmentedControl
          value={draft.destination}
          onValueChange={v => onChange(withNaming({ ...draft, destination: v === 'website' ? 'website' : 'lead_form' }))}
        >
          <SegmentedControlOption value="website">Website</SegmentedControlOption>
          <SegmentedControlOption value="lead_form">Instant Form</SegmentedControlOption>
        </SegmentedControl>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <SelectField
          label="Call to action"
          items={META_CTA_ITEMS}
          value={draft.callToAction}
          onValueChange={v => set('callToAction')(MetaCtaTypeSchema.parse(v))}
        />
        {draft.destination === 'website' && (
          <InputField
            {...AUTOFILL_IGNORE_PROPS}
            label="Destination URL"
            type="url"
            value={draft.destinationUrl}
            onValueChange={set('destinationUrl')}
          />
        )}
      </div>
      {draft.destination === 'lead_form' && <LeadFormCard draft={draft} onChange={onChange} enabled={metaConnected} />}
      <AudienceTargeting draft={draft} onChange={onChange} enabled={metaConnected} reviewer={canEditSettings} />
      <div className="grid grid-cols-3 gap-3">
        <InputField
          {...AUTOFILL_IGNORE_PROPS}
          label="Daily budget (USD)"
          type="number"
          value={draft.dailyBudgetUsd}
          onValueChange={set('dailyBudgetUsd')}
        />
        <InputField label="Start date" type="date" value={draft.startDate} onValueChange={set('startDate')} />
        <InputField label="End date" type="date" value={draft.endDate} onValueChange={set('endDate')} />
      </div>
      <MetaGrowthSettingsCard
        destination={draft.destination}
        onDestinationChange={destination => onChange(withNaming({ ...draft, destination }))}
        settings={draft.growthSettings}
        onChange={growthSettings => onChange({ ...draft, growthSettings })}
        editable={canEditSettings}
      />
    </div>
  );
}

function LinkedInForm({
  draft,
  onChange,
  onPostUrlCommit,
  connected,
}: {
  draft: LinkedInDraft;
  onChange: (d: LinkedInDraft) => void;
  onPostUrlCommit: (url: string) => void;
  connected: boolean;
}) {
  const set = <K extends keyof LinkedInDraft>(key: K) => (value: LinkedInDraft[K]) => onChange({ ...draft, [key]: value });
  const groups = useLinkedInCampaignGroups(connected);
  const campaigns = useLinkedInCampaigns(connected && draft.campaignGroupId !== null, draft.campaignGroupId);
  // Only Sponsored Content campaigns can boost a post; text, dynamic and other types can't.
  const templates = campaigns.data?.filter(c => c.type === BOOSTABLE_CAMPAIGN_TYPE);

  return (
    <div className="flex min-w-0 flex-1 flex-col gap-4">
      <div className="grid grid-cols-[minmax(0,1fr)_160px] gap-3">
        <InputField {...AUTOFILL_IGNORE_PROPS} label="Campaign name" value={draft.name} onValueChange={set('name')} />
        <TeamSelect value={draft.team} onChange={set('team')} />
      </div>
      <InputField
        {...AUTOFILL_IGNORE_PROPS}
        label="Post URL"
        type="url"
        description="A Clay company page post: ··· on the post → Copy link to post. Its copy and media can't be edited when boosting."
        value={draft.postUrl}
        onValueChange={set('postUrl')}
        onBlur={() => onPostUrlCommit(draft.postUrl.trim())}
      />
      <div className="grid grid-cols-2 gap-3">
        <SelectField
          label="Campaign group"
          description={!connected ? 'Connect LinkedIn to pick a group.' : 'TOFU, MOFU or BOFU. Never a new one.'}
          items={(groups.data ?? []).map(g => ({ value: g.id, label: g.name }))}
          value={draft.campaignGroupId ?? undefined}
          onValueChange={v => {
            const group = groups.data?.find(g => g.id === String(v));
            onChange({ ...draft, campaignGroupId: group?.id ?? null, campaignGroupName: group?.name ?? '', sourceCampaignId: null, sourceCampaignName: '' });
          }}
          disabled={!connected || groups.isLoading}
        />
        <SelectField
          label="Copy settings from"
          description="Its audience, exclusions and bid carry over."
          items={(templates ?? []).map(c => ({ value: c.id, label: c.name }))}
          value={draft.sourceCampaignId ?? undefined}
          onValueChange={v => {
            const campaign = templates?.find(c => c.id === String(v));
            onChange({ ...draft, sourceCampaignId: campaign?.id ?? null, sourceCampaignName: campaign?.name ?? '' });
          }}
          disabled={!draft.campaignGroupId || campaigns.isLoading}
        />
      </div>
      {draft.campaignGroupId && templates?.length === 0 && (
        <Text size="xs" variant="tertiary">
          This group has no Sponsored Content campaigns to copy. Growth sets one up in Campaign Manager first.
        </Text>
      )}
      <div className="grid grid-cols-3 gap-3">
        <InputField
          {...AUTOFILL_IGNORE_PROPS}
          label="Daily budget (USD)"
          type="number"
          value={draft.dailyBudgetUsd}
          onValueChange={set('dailyBudgetUsd')}
        />
        <InputField label="Start date" type="date" value={draft.startDate} onValueChange={set('startDate')} />
        <InputField label="End date" type="date" value={draft.endDate} onValueChange={set('endDate')} />
      </div>
      <GrowthDefaults items={LINKEDIN_GROWTH_DEFAULTS} />
    </div>
  );
}

export function PlacementToggle({ value, onChange }: { value: MediaSlot; onChange: (slot: MediaSlot) => void }) {
  return (
    <div className="flex gap-1 self-start rounded-lg bg-bg-secondary p-0.5 text-xs">
      {(
        [
          ['feed', 'Feed'],
          ['vertical', 'Stories & Reels'],
        ] as const
      ).map(([slot, label]) => (
        <button
          key={slot}
          type="button"
          onClick={() => onChange(slot)}
          className={`rounded-md px-2.5 py-1 font-medium ${value === slot ? 'bg-bg-primary shadow-sm' : 'text-content-tertiary'}`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

export interface LinkedInDraftProps {
  draft: LinkedInDraft;
  onChange: (d: LinkedInDraft) => void;
  /** The post URL to look up, committed when the field loses focus. */
  postUrl: string;
  onPostUrlCommit: (url: string) => void;
  connected: boolean;
}

export default function AdDraftPanel({
  metaDraft,
  onMetaDraftChange,
  creatives,
  onCreativesChange,
  metaConnected,
  platform = 'meta',
  onPlatformChange,
  linkedIn,
}: {
  metaDraft: MetaDraft;
  onMetaDraftChange: (d: MetaDraft) => void;
  /** Never empty. */
  creatives: DraftCreative[];
  onCreativesChange: Dispatch<SetStateAction<DraftCreative[]>>;
  metaConnected: boolean;
  /** Without these the panel is Meta-only (the request editor). */
  platform?: PlatformTab;
  onPlatformChange?: (platform: PlatformTab) => void;
  linkedIn?: LinkedInDraftProps;
}) {
  const tab: PlatformTab = linkedIn ? platform : 'meta';
  const linkedInPost = useLinkedInPost(linkedIn?.postUrl ?? '');
  const [dragging, setDragging] = useState(false);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  // The panel always gets at least one creative; the fallback only covers an empty list.
  const selected = creatives.find(c => c.key === selectedKey) ?? creatives[0] ?? newCreative([]);
  const creativeCheck = useCreativeCheck();
  // The check reviews the file just picked, so it's shown only on that creative.
  const [checkedKey, setCheckedKey] = useState<string | null>(null);
  const [previewSlot, setPreviewSlot] = useState<MediaSlot>('feed');
  const imageIntake = useImageIntake((image, key, slot) => {
    setPreviewSlot(slot);
    onCreativesChange(cs =>
      cs.map(c => {
        if (c.key !== key) return c;
        if (slot === 'vertical') return { ...c, vertical: image };
        // A vertical version of the other kind of media no longer matches.
        const sameKind = (c.vertical?.video !== undefined) === (image.video !== undefined);
        return { ...c, media: image, vertical: sameKind ? c.vertical : null };
      }),
    );
    if (slot === 'vertical' || image.width === undefined || image.height === undefined) return;
    const { width, height } = image;
    const copy = creativeCopy(metaDraft, creatives.find(c => c.key === key)?.overrides ?? NO_OVERRIDES);
    setCheckedKey(key);
    imageForReview(image.previewUrl).then(
      blob =>
        creativeCheck.mutate({
          platform: 'meta',
          kind: image.video ? 'video' : 'image',
          image: blob,
          width,
          height,
          draft: { headline: copy.headline, primaryText: copy.primaryText },
        }),
      () => creativeCheck.reset(),
    );
  });
  const takeForSelected = (file: File | undefined, slot: MediaSlot) => {
    const kind = slot === 'vertical' && selected.media ? (selected.media.video ? 'video' : 'image') : null;
    if (slot === 'vertical' && !selected.media) {
      imageIntake.fail({ slot, message: 'Add the feed version first.' });
      return;
    }
    imageIntake.take(file, selected.key, slot, kind);
  };
  const previewMedia = previewSlot === 'vertical' ? selected.vertical : selected.media;
  const savedVideoId = previewMedia?.video && !previewMedia.video.url ? previewMedia.video.id : null;
  const savedVideo = useMetaVideo(savedVideoId);
  const previewVideoUrl = previewMedia?.video?.url ?? (savedVideoId ? (savedVideo.data?.sourceUrl ?? null) : null);

  return (
    <section
      aria-label="Ad draft"
      className="flex min-h-0 flex-col overflow-hidden rounded-2xl border border-border-primary bg-bg-primary"
    >
      <div className="flex items-center gap-3 border-b border-border-primary px-4 py-2">
        <Tabs value={tab} onValueChange={v => onPlatformChange?.(v)}>
          <TabList>
            <Tab value="meta">Meta</Tab>
            {/* LinkedIn boosts are built but parked for P1, like Google. */}
            <Tab value="linkedin" disabled>
              LinkedIn <span className="font-mono text-[11px]">P1</span>
            </Tab>
            <Tab value="google" disabled>
              Google <span className="font-mono text-[11px]">P1</span>
            </Tab>
          </TabList>
        </Tabs>
        <Text size="xs" variant="tertiary" className="ml-auto">
          Preview updates as you edit
        </Text>
      </div>

      <div className="flex min-h-0 flex-1 gap-6 overflow-y-auto p-5">
        <div
          className={`w-80 shrink-0 rounded-xl ${dragging ? 'outline-dashed outline-2 outline-offset-4 outline-border-primary' : ''}`}
          onDragOver={e => {
            if (tab !== 'meta') return;
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={e => {
            if (tab !== 'meta') return;
            e.preventDefault();
            setDragging(false);
            takeForSelected(e.dataTransfer.files[0], previewSlot);
          }}
        >
          {tab === 'meta' ? (
            <div className="flex flex-col gap-3">
              <PlacementToggle value={previewSlot} onChange={setPreviewSlot} />
              {previewSlot === 'vertical' ? (
                <MetaStoryPreview
                  content={{
                    ...metaDraft,
                    ...creativeCopy(metaDraft, selected.overrides),
                    imageUrl: selected.vertical?.previewUrl ?? null,
                    videoUrl: previewVideoUrl,
                  }}
                  imageInputId={selected.media ? VERTICAL_INPUT_ID : undefined}
                />
              ) : (
                <MetaFeedPreview
                  content={{
                    ...metaDraft,
                    ...creativeCopy(metaDraft, selected.overrides),
                    imageUrl: selected.media?.previewUrl ?? null,
                    videoUrl: previewVideoUrl,
                  }}
                  imageInputId={IMAGE_INPUT_ID}
                />
              )}
            </div>
          ) : (
            <LinkedInPostPreview
              post={linkedInPost.data?.post ?? null}
              loading={linkedInPost.isFetching}
              problem={linkedInPost.data?.problem ?? (linkedInPost.error ? linkedInPost.error.message : null)}
            />
          )}
        </div>
        {tab === 'meta' ? (
          <MetaForm
            draft={metaDraft}
            onChange={onMetaDraftChange}
            creatives={creatives}
            selected={selected}
            onSelect={setSelectedKey}
            onCreativesChange={onCreativesChange}
            imageError={imageIntake.error}
            onImageFile={takeForSelected}
            creativeCheck={creativeCheck}
            showCreativeCheck={checkedKey === selected.key && selected.media?.file != null}
            metaConnected={metaConnected}
          />
        ) : (
          linkedIn && (
            <LinkedInForm
              draft={linkedIn.draft}
              onChange={linkedIn.onChange}
              onPostUrlCommit={linkedIn.onPostUrlCommit}
              connected={linkedIn.connected}
            />
          )
        )}
      </div>
    </section>
  );
}
